/**
 * Goals Tab
 *
 * Full goal management page for the operator portal:
 * - Active goals (GoalCard list)
 * - Completed goals (collapsible section)
 * - Suggested goals (SOPs from briefing that don't have goals yet)
 * - Empty state with CTA to create first goal
 * - CreateGoalSheet for goal creation flow
 *
 * Data sources:
 * - useOperatorGoals() — goal list from /api/operator/goals
 * - useOperatorBriefing() — focusSops for suggestions and SOP picker
 */

import { useState, useRef, useEffect } from 'react';
import { Target, Plus, Lightbulb, ChevronDown, RefreshCw } from 'lucide-react';
import { useDocumentTitle } from '../../lib/hooks/useDocumentTitle';
import { useOperatorGoals } from './hooks/useOperatorGoals';
import { useOperatorBriefing } from '../../hooks/useOperatorPortal';
import { GoalCard } from './components/GoalCard';
import { CreateGoalSheet } from './components/CreateGoalSheet';
import { GoalDetailSheet } from './components/GoalDetailSheet';
import { CelebrationOverlay } from './components/CelebrationOverlay';
import { OperatorEmptyState } from './components/OperatorEmptyState';
import { OperatorLoadingSkeleton } from './components/OperatorLoadingSkeleton';
import { OperatorCard } from './components/OperatorCard';
import { cn } from '../../lib/utils';
import { getScoreColor } from '../../lib/score-colors';
import { getRubricLabel } from './utils';
import type { OperatorGoalResponse } from '@guild/shared';

/** "Why" context for suggested goals based on current score */
function getSuggestionContext(score: number | null): string | null {
  if (score === null) return 'No assessments yet -- start tracking your progress';
  if (score < 40) return 'Needs attention -- this is your biggest growth area';
  if (score < 60) return 'Below target -- focused practice can make a big difference';
  if (score < 80) return 'Getting there -- a goal can help you reach Proficient';
  return null; // Score >= 80, no special context needed
}

/** Recommended target score based on current performance */
function getRecommendedTarget(score: number | null): number {
  if (score === null) return 60; // Developing
  if (score < 40) return 60;    // Developing
  if (score < 60) return 80;    // Proficient
  if (score < 80) return 80;    // Proficient
  return 100;                   // Exemplary
}

export function GoalsTab() {
  const [createSheetOpen, setCreateSheetOpen] = useState(false);
  const [prefillSopId, setPrefillSopId] = useState<string | null>(null);
  const [completedExpanded, setCompletedExpanded] = useState(false);
  const [selectedGoalId, setSelectedGoalId] = useState<string | null>(null);
  const [celebratingGoal, setCelebratingGoal] = useState<OperatorGoalResponse | null>(null);
  const prevGoalsRef = useRef<OperatorGoalResponse[]>([]);

  useDocumentTitle('My Goals');

  const goals = useOperatorGoals();
  const briefing = useOperatorBriefing();

  // Detect newly-completed goals by comparing with previous fetch
  useEffect(() => {
    const current = goals.data ?? [];
    const prev = prevGoalsRef.current;

    // Only check if we have previous data (skip first load)
    if (prev.length > 0 && current.length > 0) {
      // Build set of previously-active goal IDs
      const prevActiveIds = new Set(
        prev.filter(g => g.status === 'active').map(g => g.id)
      );

      // Find goals that were active before but are now completed
      const newlyCompleted = current.filter(
        g => g.status === 'completed' && prevActiveIds.has(g.id)
      );

      // Celebrate the first one (if multiple, only celebrate first)
      if (newlyCompleted.length > 0 && celebratingGoal === null) {
        setCelebratingGoal(newlyCompleted[0]);
      }
    }

    // Update ref for next comparison
    prevGoalsRef.current = current;
  // eslint-disable-next-line react-hooks/exhaustive-deps -- celebratingGoal intentionally excluded to prevent feedback loop
  }, [goals.data]);

  // Separate active vs completed goals
  const activeGoals = (goals.data ?? []).filter((g) => g.status === 'active');
  const completedGoals = (goals.data ?? []).filter((g) => g.status === 'completed');

  // Suggested goals: focusSops that don't have any goal (active or completed)
  // Sorted by lowest score first (biggest gap), nulls last
  const goalSopIds = new Set((goals.data ?? []).map((g) => g.sopId));
  const suggestedSops = (briefing.data?.focusSops ?? [])
    .filter((s) => !goalSopIds.has(s.id))
    .sort((a, b) => {
      if (a.latestScore === null && b.latestScore === null) return 0;
      if (a.latestScore === null) return 1;
      if (b.latestScore === null) return -1;
      return a.latestScore - b.latestScore;
    });

  // Available SOPs for creation sheet (focusSops minus those with active goals)
  const activeGoalSopIds = new Set(activeGoals.map((g) => g.sopId));
  const availableSops = (briefing.data?.focusSops ?? []).filter(
    (s) => !activeGoalSopIds.has(s.id),
  );

  const hasAnyGoals = (goals.data ?? []).length > 0;

  // Find selected goal from cached list (may be null if deleted between fetches)
  const selectedGoal = (goals.data ?? []).find((g) => g.id === selectedGoalId) ?? null;

  // Show skeleton while either query is loading
  if (goals.isLoading || briefing.isLoading) {
    return <OperatorLoadingSkeleton variant="cards" />;
  }

  // Error state
  if (goals.isError) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-8 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-50">
          <RefreshCw size={24} strokeWidth={1.5} className="text-red-400" />
        </div>
        <p className="mt-4 text-base font-semibold text-op-text-primary">
          Could not load goals
        </p>
        <p className="mt-1 text-sm text-op-text-secondary">
          Please check your connection and try again.
        </p>
        <button
          type="button"
          onClick={() => goals.refetch()}
          className="mt-4 min-h-[44px] rounded-xl bg-guild-green px-6 py-2 text-sm font-semibold text-white active:bg-guild-green/90 focus-visible:ring-2 focus-visible:ring-guild-green focus-visible:ring-offset-2"
        >
          Retry
        </button>
      </div>
    );
  }

  // Empty state: no goals and no suggestions
  if (!hasAnyGoals && suggestedSops.length === 0) {
    return (
      <>
        <OperatorEmptyState
          icon={Target}
          title="No Goals Yet"
          description="Set your first skill goal to start tracking your progress."
          ctaLabel="+ Add a Goal"
          onCtaClick={() => {
            setPrefillSopId(null);
            setCreateSheetOpen(true);
          }}
        />
        <CreateGoalSheet
          isOpen={createSheetOpen}
          onClose={() => setCreateSheetOpen(false)}
          availableSops={availableSops}
          hasExistingGoals={hasAnyGoals}
          defaultSopId={prefillSopId}
        />
      </>
    );
  }

  const openCreateSheet = (sopId?: string) => {
    setPrefillSopId(sopId ?? null);
    setCreateSheetOpen(true);
  };

  return (
    <>
      <div className="space-y-6">
        {/* ============================================ */}
        {/* ACTIVE GOALS SECTION                        */}
        {/* ============================================ */}
        <div>
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-op-text-primary">My Goals</h2>
            <button
              type="button"
              onClick={() => openCreateSheet()}
              className="inline-flex min-h-[44px] items-center gap-1.5 rounded-full bg-guild-green px-4 py-2 text-sm font-semibold text-white active:bg-guild-green/90 transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-guild-green focus-visible:ring-offset-2"
            >
              <Plus size={16} strokeWidth={2} />
              Add
            </button>
          </div>

          {activeGoals.length > 0 ? (
            <div className="space-y-3">
              {activeGoals.map((goal, i) => (
                <GoalCard key={goal.id} goal={goal} index={i} onPress={(g) => setSelectedGoalId(g.id)} />
              ))}
            </div>
          ) : hasAnyGoals ? (
            // Has completed goals but no active ones
            <div className="rounded-2xl bg-gray-50 px-4 py-6 text-center">
              <p className="text-sm text-op-text-secondary">
                No active goals. Tap "+ Add" to set a new target.
              </p>
            </div>
          ) : null}
        </div>

        {/* ============================================ */}
        {/* COMPLETED GOALS SECTION                     */}
        {/* ============================================ */}
        {completedGoals.length > 0 && (
          <div>
            <button
              type="button"
              onClick={() => setCompletedExpanded((prev) => !prev)}
              className="flex w-full min-h-[44px] items-center justify-between rounded-xl px-1 py-2 focus-visible:ring-2 focus-visible:ring-guild-green focus-visible:ring-offset-2"
            >
              <span className="flex items-center gap-2 text-sm font-semibold text-op-text-secondary">
                Completed
                <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-emerald-100 px-1.5 text-[11px] font-semibold text-emerald-700">
                  {completedGoals.length}
                </span>
              </span>
              <ChevronDown
                size={18}
                strokeWidth={1.5}
                className={cn(
                  'text-op-text-tertiary transition-transform duration-200',
                  completedExpanded && 'rotate-180',
                )}
              />
            </button>
            {completedExpanded && (
              <div className="mt-2 space-y-3">
                {completedGoals.map((goal, i) => (
                  <GoalCard key={goal.id} goal={goal} index={i} onPress={(g) => setSelectedGoalId(g.id)} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* ============================================ */}
        {/* SUGGESTED GOALS SECTION                     */}
        {/* ============================================ */}
        {suggestedSops.length > 0 && (
          <div>
            <div className="mb-3 flex items-center gap-2">
              <div className="h-px flex-1 bg-gray-200" />
              <span className="text-xs font-semibold uppercase tracking-wider text-op-text-tertiary">
                Suggested Goals
              </span>
              <div className="h-px flex-1 bg-gray-200" />
            </div>

            <div className="space-y-3">
              {suggestedSops.map((sop, i) => {
                const context = getSuggestionContext(sop.latestScore);
                const recommended = getRecommendedTarget(sop.latestScore);
                const recommendedLabel = getRubricLabel(recommended);

                return (
                  <div
                    key={sop.id}
                    style={{
                      animation: `fadeInUp 0.4s ease-out ${i < 5 ? i * 0.06 : 0}s both`,
                    }}
                  >
                    <OperatorCard>
                      <div className="flex items-start gap-3">
                        {/* Lightbulb icon */}
                        <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-600">
                          <Lightbulb size={22} strokeWidth={1.5} />
                        </div>

                        {/* Content */}
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <h3 className="text-sm font-semibold text-op-text-primary line-clamp-1">
                              {sop.title}
                            </h3>
                            {sop.latestScore !== null && (
                              <span
                                className={cn(
                                  'inline-flex flex-shrink-0 items-center rounded-full px-2 py-0.5 text-xs font-semibold',
                                  getScoreColor(sop.latestScore, 'badge'),
                                )}
                              >
                                {getRubricLabel(Math.round(sop.latestScore))}
                              </span>
                            )}
                          </div>
                          {context ? (
                            <p className="mt-0.5 text-xs text-op-text-tertiary">
                              {context}
                            </p>
                          ) : (
                            <p className="mt-0.5 text-xs text-op-text-tertiary">
                              Based on your zone assignments
                            </p>
                          )}
                          <button
                            type="button"
                            onClick={() => openCreateSheet(sop.id)}
                            className="mt-2 inline-flex min-h-[44px] items-center gap-1 rounded-full border border-guild-green px-3 py-1.5 text-xs font-semibold text-guild-green active:bg-guild-green/5 transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-guild-green focus-visible:ring-offset-2"
                          >
                            <Plus size={14} strokeWidth={2} />
                            {`Add Goal \u2192 ${recommendedLabel}`}
                          </button>
                        </div>
                      </div>
                    </OperatorCard>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Create Goal Sheet */}
      <CreateGoalSheet
        isOpen={createSheetOpen}
        onClose={() => setCreateSheetOpen(false)}
        availableSops={availableSops}
        hasExistingGoals={hasAnyGoals}
        defaultSopId={prefillSopId}
      />

      {/* Goal Detail Sheet (Phase 21.3.4) */}
      <GoalDetailSheet
        goalId={selectedGoalId}
        goal={selectedGoal}
        isOpen={selectedGoalId !== null}
        onClose={() => setSelectedGoalId(null)}
      />

      {/* Celebration Overlay (Phase 21.3.5) */}
      <CelebrationOverlay
        goal={celebratingGoal}
        onDismiss={() => setCelebratingGoal(null)}
        onSetNextGoal={() => {
          setCelebratingGoal(null);
          openCreateSheet();
        }}
      />
    </>
  );
}
